import { type ReactNode, useEffect, useRef } from "react";
import "./Popover.css";

interface PopoverProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

/**
 * ポップオーバー（O-1: 帳/設定）。フォーカストラップしない。`Tab`で外へ抜けたら閉じる
 * （screen-flow.md §9.3）。モーダルが開いた時点で閉じる制御は`OverlayProvider`側が行う。
 */
export function Popover({ open, onClose, children }: PopoverProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleFocusOut(event: FocusEvent) {
      const next = event.relatedTarget as Node | null;
      if (!next || !containerRef.current?.contains(next)) {
        onClose();
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    const container = containerRef.current;
    container?.addEventListener("focusout", handleFocusOut);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      container?.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div ref={containerRef} className="popover z-overlay-popover" role="dialog">
      {children}
    </div>
  );
}
